import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import Instructions from '../../Instructions';
import LabelCheck from '../LabelCheck';
import { contractorAuth } from '../../../lib/contractorAuth';
import {
  warrantyInWords, formatDay, LIFECYCLE_LABEL, CONDITION_LABEL, OWNERSHIP_LABEL, holderLabel, tagText } from '../../../lib/assets';
import { fmtDateTime } from '../../../lib/when';
import { FINISHED, ago, stateWord, stateTone, signedInSuffix } from '../assetView';
import { Facts } from './Device';
import HolderLicences from '../../licences/HolderLicences';
import { useFeature } from '../../../lib/features';

/**
 * OVERVIEW (the old address): the three cards that answer the questions a
 * device page is opened for - who has it and where, how it is doing, what
 * it cost and how long it is covered - then the specification, the label
 * check, the open ticket's instructions, the description and the latest
 * note. Everything deeper has a tab of its own.
 */
export default function OverviewTab() {
  const { data, asset: a, write, portal, meta, gone, scanned, reload, openAction, unlinkDevice, unlinkError, base, notes } = useOutletContext();
  const licencesOn = useFeature('licences');
  const warranty = warrantyInWords(a.warranty_end);
  const specRows = (a.spec_schema || []).map((f) => [f.label, a.specs?.[f.key]]);
  const extraSpecs = Object.entries(a.specs || {})
    .filter(([k]) => !(a.spec_schema || []).some((f) => f.key === k));
  const openTickets = (data.tickets || []).filter((t) => !FINISHED.includes(String(t.status || '').toLowerCase()));
  const f = a.facts;
  const e = data.device?.enrolment;
  const latest = notes?.notes?.[0] || null;
  const disk = (f?.disks || []).map((d) => {
    const used = d.size_gb ? Math.round(((d.size_gb - d.free_gb) / d.size_gb) * 100) : null;
    return used === null ? null : `${d.name} ${used}% full`;
  }).filter(Boolean).join(' · ');

  return (
    <div className="as-tab as-tab-overview">
      {gone && (
        <p className="as-note" data-tone="warning" style={{ marginTop: 0 }}>
          {`${LIFECYCLE_LABEL[a.lifecycle_status]} on ${formatDay(a.retired_at)}`}
          {a.retirement_reason ? ` — ${a.retirement_reason}` : ''}
        </p>
      )}
      {write && !portal && 'tag_confirmed_at' in a && scanned && (
        <LabelCheck asset={a} scanned={scanned} onDone={reload} />
      )}
      {unlinkError && <p className="as-error" role="alert">{unlinkError}</p>}
      {!a.location_id && !gone && (
        <p className="as-note" data-tone="warning" data-testid="no-location">
          No location yet
          {write && !portal && openAction && (
            <button type="button" className="btn btn-plain btn-small" style={{ marginLeft: '0.5rem' }}
              onClick={() => openAction('move')}>Set its location</button>
          )}
        </p>
      )}

      <div className="as-grid">
        <section className="as-panel" data-card="who">
          <h2>Who and where</h2>
          <Facts rows={[
            ['With', holderLabel(a) || 'Nobody'],
            ['Kept at', a.location_path ? (
              <>
                {a.location_path}
                {data.location_has_plan && a.location_id && !portal && (
                  <> <Link className="as-inline-link" to={`/assets/map/${a.location_id}`}>Show on the map</Link></>
                )}
              </>
            ) : 'Not recorded'],
            ...(a.default_location_path ? [['Default office', (
              <>
                {a.default_location_path}
                {a.default_location_id !== a.location_id && (
                  <> <span className="as-chip" data-tone="warning">Away from its default office</span></>
                )}
              </>
            )]] : []),
            ...(Array.isArray(meta?.companies) ? [['Company', a.company_name || 'None']] : []),
            ['Department', a.department_name],
            ['Hostname', a.hostname],
            ['Plugged into', a.parent_tag ? (
              <>
                <Link to={`/assets/${a.parent_asset_id}`}>{`${a.parent_tag}${a.parent_make || a.parent_model ? ` · ${[a.parent_make, a.parent_model].filter(Boolean).join(' ')}` : ''}`}</Link>
                {write && !portal && (
                  <button type="button" className="btn btn-plain btn-small" style={{ marginLeft: '0.5rem' }}
                    onClick={() => unlinkDevice(a.id)}>Unlink</button>
                )}
              </>
            ) : null],
            ['Plugged into it', data.children?.length > 0 ? (
              <>
                {data.children.map((c, i) => (
                  <React.Fragment key={c.id}>
                    {i > 0 ? ', ' : ''}
                    <Link to={`/assets/${c.id}`}>{tagText(c)}</Link>
                  </React.Fragment>
                ))}
                {' '}<Link className="as-inline-link" to={`${base}/device`}>Details</Link>
              </>
            ) : null]
          ]} />
        </section>

        <section className="as-panel" data-card="glance">
          <h2>Device at a glance</h2>
          {!data.device && !f && (
            <p className="as-quiet">
              {'ip_address' in a && (a.ip_address || a.last_seen_at)
                ? <>Seen on the network{a.last_seen_at ? ` ${ago(a.last_seen_at)}` : ''}. <Link to={`${base}/device`}>Network facts</Link></>
                : 'No agent reports from this device.'}
            </p>
          )}
          {(data.device || f) && (
            <>
              <Facts rows={[
                ['Reporting', e ? `${ago(a.last_heartbeat_at)}` : f ? 'Agent stopped' : 'No agent'],
                ['Signed in', f ? <>{f.user_upn || f.user_account || 'Nobody'}{signedInSuffix(a)}</> : null],
                ['System', f ? ([f.os, f.os_build && `build ${f.os_build}`].filter(Boolean).join(', ') || '—') : null],
                ['Encryption', f ? <><span className="as-chip" data-tone={stateTone(f.encryption)}>{stateWord(f.encryption)}</span>{f.encryption_method ? ` ${f.encryption_method}` : ''}</> : null],
                ['Disk', disk || null],
                ['Battery', f?.battery_health_pct != null ? `${f.battery_health_pct}% of its design capacity` : null],
                ['Agent', e ? `${e.platform === 'macos' ? 'Mac' : 'Windows'} ${e.agent_version || ''}`.trim() : null]
              ]} />
              <p className="as-quiet as-more-link"><Link to={`${base}/device`}>Full report</Link></p>
            </>
          )}
        </section>

        <section className="as-panel" data-card="warranty">
          <h2>Warranty and purchase</h2>
          <p style={{ margin: '0 0 0.5rem' }}><span className="as-chip" data-tone={warranty.tone}>{warranty.text}</span></p>
          <Facts rows={[
            ['From', a.warranty_provider],
            ['Reference', a.warranty_ref],
            ['Starts', a.warranty_start ? formatDay(a.warranty_start) : null],
            ['Replace by', a.planned_refresh_date ? formatDay(a.planned_refresh_date) : null],
            ['Ownership', OWNERSHIP_LABEL[a.ownership]],
            ['Bought', a.purchase_date ? formatDay(a.purchase_date) : null],
            ['Supplier', a.supplier],
            ['Invoice', a.invoice_ref],
            ['Cost', a.cost_ex_vat !== null && a.cost_ex_vat !== undefined
              ? `${Number(a.cost_ex_vat).toFixed(2)} ${a.currency} excl. VAT` : null]
          ]} />
        </section>
      </div>

      {/* 119: the licences on this device - an administrator's, like the
          register, and only while the Licences switch is on. */}
      {meta?.access?.admin && !portal && licencesOn && <HolderLicences kind="asset" id={a.id} compact card />}

      <section className="as-panel" data-card="spec">
        <h2>Specification</h2>
        <Facts rows={[
          ['Make', a.manufacturer], ['Model', a.model], ['Model number', a.model_number],
          ['Serial number', a.serial_number],
          ['Condition', CONDITION_LABEL[a.condition]],
          ...specRows, ...extraSpecs
        ]} />
        {a.last_heartbeat_at && (
          <p className="as-quiet">Last reported in {formatDay(a.last_heartbeat_at)}</p>
        )}
      </section>

      {write && !portal && 'tag_confirmed_at' in a && !scanned && (
        <LabelCheck asset={a} scanned={false} onDone={reload} />
      )}
      {write && !portal && a.asset_tag && 'label_printed_at' in a && (
        <p className="as-quiet" data-testid="label-printed">
          {a.label_printed_at ? `Label printed ${formatDay(a.label_printed_at)}. ` : 'Label not printed yet. '}
          <Link to={`/assets/labels?ids=${a.id}`}>Print / reprint label</Link>
        </p>
      )}

      {/* Instructions belong to a ticket, so one panel per open ticket on the
          device - here as well as on the ticket, because somebody who looks
          after this device may not be a party to the ticket and cannot open
          it. Each panel asks the server, which decides who may send. */}
      {data.device && write && openTickets
        .map((t) => <Instructions key={t.id} ticketId={t.id} title={`Instructions for ${t.ticket_number}`}
          authHeaders={contractorAuth()?.headers || null} />)}
      {/* With no open ticket, the register itself is where an instruction is
          sent from (063): to the holder or a contractor, and Jarvis raises
          the ticket it is kept with. */}
      {write && !gone && openTickets.length === 0 && (
        <Instructions assetId={a.id} title="Instructions" authHeaders={contractorAuth()?.headers || null} />
      )}

      {a.notes && (
        <section className="as-panel" data-card="description">
          <h2>Description</h2>
          <p className="as-description">{a.notes}</p>
        </section>
      )}

      {notes && notes.status !== 503 && (
        <section className="as-panel" data-card="latest-note">
          <h2>{latest ? 'Latest note' : 'Notes'}</h2>
          {latest ? (
            <p className="as-latest-note">
              <span className="as-note-preview">{latest.body}</span>
              <span className="as-when">{`${latest.author_name} · ${fmtDateTime(latest.created_at)}`}</span>
            </p>
          ) : <p className="as-quiet">No notes yet.</p>}
          <p className="as-quiet as-more-link"><Link to={`${base}/notes`}>{latest ? 'All notes' : 'Add one'}</Link></p>
        </section>
      )}
    </div>
  );
}
